/**
 * WatcherCheckRunner — one scheduled watcher check.
 *
 * A check is a normal native `start` of the campaign's locked one-node
 * `watcher` tree in `local-shadow` mode (observe only, never sends input):
 *   `completed`               → pass, with the matched row's difference ratio
 *   `failed` + `node_deadline` → mismatch, with the best observed ratio
 *   anything else              → error (the watcher retries next tick)
 * A tick that arrives while a macro run is active starts nothing.
 */

import type { MacroMeasurement, MacroRunReply, WatcherResult } from '@playcastdotio/Messaging';
import type { MacroBundleLoad, MacroBundleSocketLoader } from '../../services/playjector/MacroBundleSocketLoader';
import type { NativeMacroBridge } from '../../services/playjector/NativeMacroBridge';

export interface WatcherCheckRunnerDeps {
  loader: Pick<MacroBundleSocketLoader, 'load'>;
  bridge: Pick<NativeMacroBridge, 'start' | 'cancel'>;
  /** True while another native macro run owns Playjector. */
  busy: () => boolean;
  newRunId: () => string;
}

const MAX_ERROR_CODE_LENGTH = 128;

type Ratio = Pick<MacroMeasurement, 'differenceNumerator' | 'differenceDenominator'>;

function ratioOf(row: Ratio): Pick<WatcherResult, 'differenceNumerator' | 'differenceDenominator'> {
  return { differenceNumerator: row.differenceNumerator, differenceDenominator: row.differenceDenominator };
}

function isSmaller(candidate: Ratio, best: Ratio): boolean {
  return candidate.differenceNumerator * best.differenceDenominator < best.differenceNumerator * candidate.differenceDenominator;
}

/** The row the engine accepted; the last `matched` row wins when several were emitted. */
function matchedRow(measurements: MacroMeasurement[]): MacroMeasurement | undefined {
  for (let index = measurements.length - 1; index >= 0; index -= 1) {
    if (measurements[index].decision === 'matched') return measurements[index];
  }
  return undefined;
}

/** The smallest difference the engine observed before the deadline. */
function bestObservedRow(measurements: MacroMeasurement[]): MacroMeasurement | undefined {
  let best: MacroMeasurement | undefined;
  for (const row of measurements) if (!best || isSmaller(row, best)) best = row;
  return best;
}

export class WatcherCheckRunner {
  private activeRunId: string | null = null;
  /** The bridge can settle `start` when cancellation fails, before release is confirmed. */
  private cancellationRunId: string | null = null;
  /** Bumped by `cancel()`; a check that observes a newer generation after its load never starts a run. */
  private cancelGeneration = 0;

  constructor(private readonly deps: WatcherCheckRunnerDeps) {}

  public async check(campaignId: string): Promise<WatcherResult> {
    if (this.cancellationRunId && !await this.cancel()) return this.unknownError(campaignId, 'input_release_unconfirmed');
    if (this.deps.busy()) return this.unknownError(campaignId, 'native_run_busy');
    const generation = this.cancelGeneration;

    let loaded: MacroBundleLoad;
    try {
      loaded = await this.deps.loader.load(campaignId, 'watcher');
    } catch (error) {
      return this.unknownError(campaignId, WatcherCheckRunner.errorCode(error));
    }
    if (generation !== this.cancelGeneration) return this.unknownError(campaignId, 'watcher_cancelled');
    if ('error' in loaded) return this.unknownError(campaignId, loaded.error);
    if (this.deps.busy()) return this.unknownError(campaignId, 'native_run_busy');

    const { bundle, assets } = loaded;
    const identity = { campaignId, revision: bundle.revision, artifactDigest: bundle.artifactDigest };
    const runId = this.deps.newRunId();
    this.activeRunId = runId;
    try {
      const reply = await this.deps.bridge.start(runId, bundle, 'local-shadow', assets);
      return { ...identity, ...WatcherCheckRunner.mapReply(reply) };
    } catch (error) {
      return { ...identity, outcome: 'error', errorCode: WatcherCheckRunner.errorCode(error) };
    } finally {
      if (this.activeRunId === runId) this.activeRunId = null;
    }
  }

  /**
   * Cancels the current check: a run in flight is released through the bridge;
   * a check still loading its bundle will not start. Resolves true when nothing
   * was running or release was confirmed.
   */
  public async cancel(): Promise<boolean> {
    this.cancelGeneration += 1;
    const runId = this.activeRunId ?? this.cancellationRunId;
    if (!runId) return true;
    this.cancellationRunId = runId;
    const released = await this.deps.bridge.cancel(runId).catch(() => false);
    if (released) {
      if (this.activeRunId === runId) this.activeRunId = null;
      if (this.cancellationRunId === runId) this.cancellationRunId = null;
    }
    return released;
  }

  private static mapReply(reply: MacroRunReply): Omit<WatcherResult, 'campaignId' | 'revision' | 'artifactDigest'> {
    const measurements = reply.measurements ?? [];
    if (reply.outcome === 'completed') {
      const matched = matchedRow(measurements);
      return { outcome: 'pass', ...(matched ? ratioOf(matched) : {}) };
    }
    if (reply.outcome === 'failed' && reply.errorCode === 'node_deadline') {
      const best = bestObservedRow(measurements);
      return { outcome: 'mismatch', ...(best ? ratioOf(best) : {}) };
    }
    return { outcome: 'error', errorCode: (reply.errorCode ?? `native_run_${reply.outcome}`).slice(0, MAX_ERROR_CODE_LENGTH) };
  }

  private static errorCode(error: unknown): string {
    const message = error instanceof Error ? error.message : String(error);
    return `watcher_check_threw:${message}`.slice(0, MAX_ERROR_CODE_LENGTH);
  }

  private unknownError(campaignId: string, errorCode: string): WatcherResult {
    // Errors raised before a bundle is known carry no revision or artifact digest.
    return { campaignId, outcome: 'error', errorCode: errorCode.slice(0, MAX_ERROR_CODE_LENGTH) };
  }
}
